import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { registry } from './registry';

export interface PopupLayerProps {
  className?: string;
  name?: string;
  mode?: 'single' | 'multiple';
}

export interface PopupInternalState {
  items: PopupItem[];
  index: Record<number, PopupItem>;
  visible: PopupItem[];
  active: boolean;
}

export interface PopupItem {
  id: number;
  children: React.ReactNode;
  props: any;
  name?: string;
  sender: {
    id: number;
    resolve: (result: any) => void;
    reject: (reason?: any) => void;
  };
}

const MODE_SINGLE = 'single';

export const PopupLayer = (props: PopupLayerProps) => {
  const [internalState, setInternalState] = useState<PopupInternalState>({
    items: [],
    index: {},
    visible: [],
    active: false,
  });
  const nodeRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  const transitionRefs = useRef<Record<number, React.RefObject<HTMLDivElement>>>({});

  const updateItems = useCallback((items: PopupItem[], index: Record<number, PopupItem>) => {
    const visible = items.filter((item) => !!item.props.open);
    const newState = {
      items,
      index,
      visible,
      active: visible.length > 0,
    };
    setInternalState(newState);
    registry.checkHooks();
  }, []);

  const updateWindow = useCallback(
    (
      sender: { id: number; resolve: (result: any) => void; reject: (reason?: any) => void },
      children: React.ReactNode,
      props: any,
      name?: string,
    ) => {
      if (!mountedRef.current) return;

      const id = sender.id;
      const { items, index } = internalState;
      const item = index[id];
      let i = items.length;

      if (item) {
        i = items.indexOf(item);
        if (i === -1) {
          throw new Error('[PopupLayer] Window not found');
        }
      }

      const newItem = { id, children, props, name, sender };
      const newItems = items.slice();
      if (i === newItems.length) {
        newItems.push(newItem);
      } else {
        newItems[i] = newItem;
      }

      const newIndex = { ...index, [id]: newItem };
      updateItems(newItems, newIndex);
    },
    [internalState, updateItems],
  );

  const removeWindow = useCallback(
    (id: number) => {
      if (!mountedRef.current) return;

      const { items, index } = internalState;
      const item = index[id];
      if (!item) {
        return;
      }

      const newItems = [...items];
      const i = newItems.indexOf(item);
      if (i >= 0) {
        newItems.splice(i, 1);
      }

      const newIndex = { ...index };
      delete newIndex[id];
      updateItems(newItems, newIndex);
    },
    [internalState, updateItems],
  );

  const rejectAll = useCallback(() => {
    internalState.visible.forEach((item) => {
      item.sender.reject();
    });
  }, [internalState.visible]);

  const resolve = useCallback((item: PopupItem, result: any) => {
    item.sender.resolve(result);
  }, []);

  const reject = useCallback((item: PopupItem, reason?: any) => {
    item.sender.reject(reason);
  }, []);

  const rejectLast = useCallback(() => {
    if (internalState.visible.length) {
      reject(internalState.visible[internalState.visible.length - 1]);
      return true;
    }
    return false;
  }, [internalState.visible, reject]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (props.mode === MODE_SINGLE && e.target === nodeRef.current) {
        rejectLast();
      }
    },
    [props.mode, rejectLast],
  );

  useEffect(() => {
    const layerApi = {
      internalState,
      updateWindow,
      removeWindow,
      rejectLast,
    };

    registry.addLayer(layerApi);
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      rejectAll();
      if (internalState.active) {
        registry.layerHidden();
      }
      registry.removeLayer(layerApi);
    };
  }, [internalState, updateWindow, removeWindow, rejectLast, rejectAll]);

  const visibleItems = React.useMemo(() => {
    const first = Math.max(0, internalState.visible.length - 1);
    return internalState.visible.slice(first).map((item) => {
      if (!transitionRefs.current[item.id]) {
        // @ts-ignore
        transitionRefs.current[item.id] = React.createRef();
      }
      return {
        ...item,
        ref: transitionRefs.current[item.id],
        children: React.cloneElement(item.children as React.ReactElement, {
          // @ts-ignore
          ...(item.children as React.ReactElement).props,
          resolve: (result: any) => resolve(item, result),
          reject: (reason?: any) => reject(item, reason),
        }),
      };
    });
  }, [internalState.visible, resolve, reject]);

  return (
    <div
      id={props.name}
      className={clsx('c-by-insignia-common-popup-layer', props.className, {
        'c-by-insignia-common-popup-layer--visible': visibleItems.length > 0,
        'c-by-insignia-common-popup-layer--use-backdrop': props.mode === MODE_SINGLE,
      })}
      onClick={handleBackdropClick}
      ref={nodeRef}
    >
      <TransitionGroup component={null}>
        {visibleItems.map((item) => (
          <CSSTransition key={item.id} timeout={600} classNames='c-by-insignia-common-popup-layer__window' nodeRef={item.ref} unmountOnExit>
            <div className='c-by-insignia-popup-window' ref={item.ref}>
              {item.children}
            </div>
          </CSSTransition>
        ))}
      </TransitionGroup>
    </div>
  );
};
